import { useCallback } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { runningTaskKeysState } from '~/state/atoms/runningTasksAtoms';

/** Tasks running per key, such as a download per dataset. */
export interface RunningTasks {
    /** Whether a task started on `key` is still running. */
    readonly isRunning: (key: string) => boolean;
    /**
     * Starts `task` on `key`, which counts as running until the task
     * settles. A key that is already running starts nothing. The task
     * reports its own failure to the user; a rejection that still escapes
     * it only ends the running state.
     */
    readonly run: (key: string, task: () => Promise<unknown>) => void;
}

/**
 * Tracks tasks by the item they run on, app-wide: a running task shows on
 * every control that offers it for its item, also on a control mounted
 * after the task started, and the item cannot start the task twice. Keys
 * are shared by every caller, so each names its item uniquely.
 */
export function useRunningTasks(): RunningTasks {
    const running = useRecoilValue(runningTaskKeysState);
    const run = useRecoilCallback(
        ({ snapshot, set }) =>
            (key: string, task: () => Promise<unknown>) => {
                if (
                    snapshot
                        .getLoadable(runningTaskKeysState)
                        .getValue()
                        .has(key)
                ) {
                    return;
                }
                set(runningTaskKeysState, (current) =>
                    new Set(current).add(key)
                );
                void task()
                    .catch(() => undefined)
                    .finally(() =>
                        set(runningTaskKeysState, (current) => {
                            const next = new Set(current);
                            next.delete(key);
                            return next;
                        })
                    );
            },
        []
    );
    const isRunning = useCallback((key: string) => running.has(key), [running]);
    return { isRunning, run };
}
